<template>
  <!-- Breadcrumb Navigation -->
  <div class="tw-p-2 tw-bg-white tw-w-full tw-border tw-border-solid tw-flex tw-items-center tw-gap-2">
    <router-link :to="{ name: 'pages.admin.dashboard' }"
      class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
      <icon icon="solar:home-smile-bold-duotone" /> 
      <span class="tw-text-sm">{{ $t("dashboard.sidebar.dashboard") }}</span>
    </router-link>
    <span class="tw-text-gray-300">/</span>
    <button disabled class="tw-text-primary-500 tw-text-xl tw-flex tw-items-center tw-gap-2">
      <span class="tw-text-sm">{{ $t("dashboard.sidebar.sessions") }}</span>
    </button>
  </div>

  <!-- Main Content -->
  <div class="tw-p-2">
    <div class="tw-h-full tw-w-full tw-p-4x tw-bg-whitex tw-rounded tw-border-b-2 tw-min-h-[calc(100dvh-125px)]">
      
      <!-- Header Section -->
      <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
        <div class="tw-flex tw-items-center tw-gap-4">
          <!-- Search Field -->
          <FormField 
            v-model="search" 
            type="text" 
            :hide-label="true" 
            :hide-errors="true" 
            :placeholder="$t('common.sessions-search-placeholder', 'Search sessions...')"
            left-icon="solar:magnifer-linear" 
            @input="onSearch" 
            class="tw-bg-white tw-rounded-md tw-min-w-[300px]" 
          />

          <!-- Status Filter -->
          <FormSelect
            v-if="false"
            v-model="filters.status" 
            :options="statusOptions"
            :placeholder="$t('common.filter-by-status', 'Filter by status')"
            left-icon="solar:filter-bold-duotone" 
            class="tw-bg-white tw-rounded-md tw-min-w-[180px]"
          />

          <!-- Type Filter -->
          <FormSelect
            v-if="false"
            v-model="filters.type" 
            :options="typeOptions"
            :placeholder="$t('common.filter-by-type', 'Filter by type')"
            left-icon="solar:card-bold-duotone" 
            class="tw-bg-white tw-rounded-md tw-min-w-[180px]"
          />

          <!-- Language Filter -->
          <FormSelect
            v-if="false"
            v-model="filters.language" 
            :options="languageOptions"
            :placeholder="$t('common.filter-by-language', 'Filter by language')"
            left-icon="solar:translation-bold-duotone" 
            class="tw-bg-white tw-rounded-md tw-min-w-[180px]"
          />
        </div>

        <!-- Create Session Button -->
        <div>
          <a-button 
            as="router-link" 
            :to="{ name: 'pages.admin.sessions.create' }" 
            intent="flat" 
            left-icon="solar:add-circle-bold-duotone"
            class="tw-min-w-[160px]"
          >
            {{ $t('common.create-new-session', 'Create New Session') }}
          </a-button>
        </div>
      </div>
      
      <!-- Sessions Table -->
      <AdminSessionTable 
        :loading="loading" 
        :items="sessions" 
        :options="paginationOptions" 
        @next="onNext"
        @previous="onPrevious" 
        @edit="onEdit" 
        @delete="onDelete" 
      />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField.vue';
import FormSelect from '@/components/form/FormSelect.vue';
import AdminSessionTable from '@/components/dashboard/admin/sessions/table/AdminSessionTable.vue';
import sessionApi from '@/api/sessions';
import useAlert from '@/composables/useAlert';

const { t } = useI18n();

// Reactive data
const loading = ref(false);
const sessions = ref([]);
const search = ref('');

// Pagination options
const paginationOptions = ref({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    next_page_url: null,
    prev_page_url: null
});

// Filters
const filters = reactive({
    status: '',
    type: '',
    language: ''
});

// Filter options
const statusOptions = ref([
    { id: '', name: t('common.all') },
    { id: 'draft', name: t('common.draft', 'Draft') },
    { id: 'scheduled', name: t('common.scheduled', 'Scheduled') },
    { id: 'live', name: t('common.live', 'Live') },
    { id: 'completed', name: t('common.completed', 'Completed') },
    { id: 'cancelled', name: t('common.cancelled', 'Cancelled') }
]);

const typeOptions = ref([
    { id: '', name: t('common.all') }
]);

const languageOptions = ref([
    { id: '', name: t('common.all') },
    { id: '1', name: 'English' },
    { id: '2', name: 'Arabic' },
    { id: '3', name: 'Spanish' },
    { id: '4', name: 'French' },
    { id: '5', name: 'Portuguese' }
]);

// Search debounce
let searchTimeout = null;
let filterTimeout = null;

// Watch for filter changes
watch(() => filters.status, () => {
    clearTimeout(filterTimeout);
    filterTimeout = setTimeout(() => {
        fetchSessions(1);
    }, 300);
}, { immediate: false });

watch(() => filters.type, () => {
    clearTimeout(filterTimeout);
    filterTimeout = setTimeout(() => {
        fetchSessions(1);
    }, 300);
}, { immediate: false });

watch(() => filters.language, () => {
    clearTimeout(filterTimeout);
    filterTimeout = setTimeout(() => {
        fetchSessions(1);
    }, 300);
}, { immediate: false });

// Methods
const fetchSessions = async (page = 1) => {
    loading.value = true;

    try {
        const params = {
            page,
            per_page: paginationOptions.value.per_page,
            search: search.value,
            ...filters
        };

        // Remove empty filters
        Object.keys(params).forEach(key => {
            if (params[key] === '' || params[key] === null || params[key] === undefined) {
                delete params[key];
            }
        });

        const response = await sessionApi.admin.paginate(params);
        
        if (response.data) {
            // Extract the pagination object directly from response.data
            const paginationData = response.data;
            
            // Extract the actual sessions array from response.data.data
            if (paginationData.data && Array.isArray(paginationData.data)) {
                sessions.value = paginationData.data;
            } else {
                console.error('Expected sessions array but got:', typeof paginationData.data, paginationData.data);
                sessions.value = [];
            }
            
            // Set pagination options from the pagination data with all expected fields
            paginationOptions.value = {
                current_page: paginationData.current_page || 1,
                per_page: paginationData.per_page || 10,
                total: paginationData.total || 0,
                last_page: paginationData.last_page || 1,
                next_page_url: paginationData.next_page_url || null,
                prev_page_url: paginationData.prev_page_url || null,
                from: paginationData.from || null,
                to: paginationData.to || null,
                links: paginationData.links || [],
                path: paginationData.path || null,
                first_page_url: paginationData.first_page_url || null,
                last_page_url: paginationData.last_page_url || null
            };
        } else {
            console.error('Invalid response structure:', response);
            sessions.value = [];
        }
    } catch (error) {
        console.error('Error fetching sessions:', error);
        console.error('Error details:', error.response);
        useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger');
        sessions.value = [];
    } finally {
        loading.value = false;
    }
};

const onSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        fetchSessions(1);
    }, 500);
};

const onNext = () => {
    if (paginationOptions.value.current_page < paginationOptions.value.last_page) {
        fetchSessions(paginationOptions.value.current_page + 1);
    }
};

const onPrevious = () => {
    if (paginationOptions.value.current_page > 1) {
        fetchSessions(paginationOptions.value.current_page - 1);
    }
};

const onEdit = (session) => {
    // Edit is handled by the action button routing
    console.log('Edit session:', session.id);
};

const onDelete = (session) => {
    // Delete functionality can be implemented here if needed
    console.log('Delete session:', session.id);
};

// Fetch filter options (types and topics could come from API)
const fetchFilterOptions = async () => {
    try {
        // You can implement these API calls if they exist
        // For now, we'll keep the basic options
        typeOptions.value = [
            { id: '', name: t('common.all') },
            { id: 'webinar', name: t('common.webinar', 'Webinar') },
            { id: 'workshop', name: t('common.workshop', 'Workshop') },
            { id: 'conference', name: t('common.conference', 'Conference') },
            { id: 'meeting', name: t('common.meeting', 'Meeting') }
        ];
    } catch (error) {
        console.error('Error fetching filter options:', error);
    }
};

// Initialize
onMounted(() => {
    fetchSessions();
    fetchFilterOptions();
});
</script>

<style scoped>
/* Add any custom styles here */
</style>